import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Code,
  ColorInput,
  Container,
  Group,
  List,
  NumberInput,
  Select,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  normalizePaletteHex,
  normalizePaletteSlotName,
  paletteSlotFromMaterialName,
} from "../../shared/furniture-palette";
import {
  DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY,
  DEFAULT_FURNITURE_CATALOG_COLLECTION_KEY,
  DRAFT_LIBRARY_CATEGORY_KEY,
  DRAFT_LIBRARY_COLLECTION_KEY,
  FURNITURE_CATALOG_CATEGORIES,
  listCollectionsForCategory,
  normalizeCatalogCategoryKey,
  normalizeCatalogCollectionKey,
} from "../../shared/furniture-catalog";
import type {
  FurnitureColorVariant,
  FurnitureDefinition,
  FurnitureInteractionType,
  FurnitureItemState,
} from "../../shared/types";
import {
  createAdminFurniture,
  updateAdminFurniture,
  uploadAdminFurnitureModel,
  validateAdminFurnitureModel,
  type AdminFurnitureInput,
  type Session,
} from "../lib/api";
import { FurniturePreviewCanvas } from "../components/FurniturePreviewCanvas";
import React from "react";

interface Props {
  session: Session;
  onCreated: () => Promise<void>;
  editingFurniture?: FurnitureDefinition | null;
  onDoneEditing?: () => void;
}

interface ColorVariantDraft {
  id: string;
  name: string;
  colors: Record<string, string>;
  priceCoins: number;
  active: boolean;
}

interface CreatorDraft {
  name: string;
  description: string;
  modelUrl: string;
  priceCoins: number;
  artist: string;
  blockWidth: number;
  blockDepth: number;
  interactionType: FurnitureInteractionType;
  walkableTop: boolean;
  stackableTop: boolean;
  stackHeight: number;
  actionOpenAnimation: string;
  actionCloseAnimation: string;
  seatOffsetX: number;
  seatOffsetY: number;
  seatOffsetZ: number;
  colorSlots: string[];
  colorVariants: ColorVariantDraft[];
  defaultColorVariantId: string;
  catalogCategory: string;
  catalogCollection: string;
  itemState: FurnitureItemState;
}

interface AutoDetectedModelMetrics {
  widthMeters: number;
  depthMeters: number;
  heightMeters: number;
  blockWidth: number;
  blockDepth: number;
  stackHeight: number;
  paletteSlots: string[];
  paletteDefaultColors: Record<string, string>;
}

const DOOR_OPEN_CLIP_NAME = "door_open";
const DOOR_CLOSE_CLIP_NAME = "door_close";
const DOOR_REQUIRED_CLIP_NAMES = [
  DOOR_OPEN_CLIP_NAME,
  DOOR_CLOSE_CLIP_NAME,
] as const;
const TELEPORT_EFFECTS_CLIP_NAME = "teleport_effects";
const TELEPORT_REQUIRED_CLIP_NAMES = [TELEPORT_EFFECTS_CLIP_NAME] as const;
const AUTO_DETECT_TILE_MAX = 8;
const AUTO_DETECT_STACK_HEIGHT_MAX = 8;
const PALETTE_VARIANT_MAX = 16;
const CREATOR_EXAMPLE_COLOR_VARIANTS: FurnitureColorVariant[] = [
  {
    id: "beige",
    name: "Beige",
    colors: {
      primary: "#d8c7a5",
    },
  },
  {
    id: "teal",
    name: "Teal",
    colors: {
      primary: "#2c9c94",
    },
  },
];

function createVariantId(): string {
  return `variant-${crypto.randomUUID().slice(0, 8)}`;
}

function normalizeColorSlotList(slots: string[]): string[] {
  const normalized = new Set<string>();
  for (const raw of slots) {
    const slot = normalizePaletteSlotName(raw);
    if (slot) {
      normalized.add(slot);
    }
  }
  return Array.from(normalized);
}

function createColorVariantDraft(
  slots: string[],
  defaults: Record<string, string> = {},
  priceCoins = 200,
  fallbackName = "Base Variant"
): ColorVariantDraft {
  const colors: Record<string, string> = {};
  for (const slot of slots) {
    const defaultColor = normalizePaletteHex(defaults[slot] ?? "") ?? "#ffffff";
    colors[slot] = defaultColor;
  }
  return {
    id: createVariantId(),
    name: fallbackName,
    colors,
    priceCoins: Math.max(1, Math.round(priceCoins)),
    active: true,
  };
}

function normalizeVariantForSlots(
  variant: ColorVariantDraft,
  slots: string[],
  defaults: Record<string, string> = {}
): ColorVariantDraft {
  const colors: Record<string, string> = {};
  for (const slot of slots) {
    const candidate = normalizePaletteHex(variant.colors[slot] ?? "");
    const fallback = normalizePaletteHex(defaults[slot] ?? "");
    colors[slot] = candidate ?? fallback ?? "#ffffff";
  }
  return {
    id: variant.id,
    name: variant.name,
    colors,
    priceCoins: Math.max(1, Math.round(variant.priceCoins)),
    active: variant.active !== false,
  };
}

function createDefaultDraft(
  artistAddress: string,
  isAdmin: boolean
): CreatorDraft {
  const catalogCategory = isAdmin
    ? DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY
    : DRAFT_LIBRARY_CATEGORY_KEY;
  const catalogCollection = isAdmin
    ? DEFAULT_FURNITURE_CATALOG_COLLECTION_KEY
    : DRAFT_LIBRARY_COLLECTION_KEY;
  return {
    name: "",
    description: "",
    modelUrl: "",
    priceCoins: 200,
    artist: artistAddress.trim(),
    blockWidth: 1,
    blockDepth: 1,
    interactionType: "none",
    walkableTop: false,
    stackableTop: false,
    stackHeight: 1,
    actionOpenAnimation: "",
    actionCloseAnimation: "",
    seatOffsetX: 0,
    seatOffsetY: 0.34,
    seatOffsetZ: 0,
    colorSlots: [],
    colorVariants: [],
    defaultColorVariantId: "",
    catalogCategory,
    catalogCollection,
    itemState: isAdmin ? "published" : "draft",
  };
}

function resolveInteractionType(
  item: FurnitureDefinition
): FurnitureInteractionType {
  return item.interactionType ?? (item.sittable ? "sit" : "none");
}

function createDraftFromFurniture(
  item: FurnitureDefinition,
  artistAddress: string,
  isAdmin: boolean
): CreatorDraft {
  const interactionType = resolveInteractionType(item);
  const colorSlots = normalizeColorSlotList(item.colorSlots ?? []);
  const incomingVariants = Array.isArray(item.colorVariants)
    ? item.colorVariants
    : [];
  const colorVariants = incomingVariants
    .map((variant) => {
      const colors: Record<string, string> = {};
      for (const slot of colorSlots) {
        const normalizedColor = normalizePaletteHex(
          variant.colors?.[slot] ?? ""
        );
        colors[slot] = normalizedColor ?? "#ffffff";
      }
      return {
        id: variant.id?.trim() || createVariantId(),
        name: variant.name?.trim() || "Variant",
        colors,
        priceCoins:
          typeof variant.priceCoins === "number" &&
          Number.isFinite(variant.priceCoins)
            ? Math.max(1, Math.round(variant.priceCoins))
            : Math.max(1, Math.round(item.priceCoins ?? 1)),
        active: variant.active !== false,
      } satisfies ColorVariantDraft;
    })
    .slice(0, PALETTE_VARIANT_MAX);
  const defaultColorVariantId =
    colorVariants.find((variant) => variant.id === item.defaultColorVariantId)
      ?.id ??
    colorVariants[0]?.id ??
    "";
  const fallbackCategory = isAdmin
    ? DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY
    : DRAFT_LIBRARY_CATEGORY_KEY;
  const category = normalizeCatalogCategoryKey(
    item.catalogCategory ?? fallbackCategory
  );
  const fallbackCollection = isAdmin
    ? DEFAULT_FURNITURE_CATALOG_COLLECTION_KEY
    : DRAFT_LIBRARY_COLLECTION_KEY;
  const collection = normalizeCatalogCollectionKey(
    item.catalogCollection ?? fallbackCollection,
    category
  );
  return {
    name: item.name ?? "",
    description: item.description ?? "",
    modelUrl: item.modelUrl ?? "",
    priceCoins: Math.max(1, Math.round(item.priceCoins ?? 1)),
    artist: (item.artist ?? artistAddress).trim(),
    blockWidth: Math.max(1, Math.round(item.blockWidth ?? 1)),
    blockDepth: Math.max(1, Math.round(item.blockDepth ?? 1)),
    interactionType,
    walkableTop: item.walkableTop ?? false,
    stackableTop: item.stackableTop ?? false,
    stackHeight:
      typeof item.stackHeight === "number" && Number.isFinite(item.stackHeight)
        ? item.stackHeight
        : 1,
    actionOpenAnimation: item.actionOpenAnimation ?? "",
    actionCloseAnimation: item.actionCloseAnimation ?? "",
    seatOffsetX:
      typeof item.seatOffsetX === "number" && Number.isFinite(item.seatOffsetX)
        ? item.seatOffsetX
        : 0,
    seatOffsetY:
      typeof item.seatOffsetY === "number" && Number.isFinite(item.seatOffsetY)
        ? item.seatOffsetY
        : 0.34,
    seatOffsetZ:
      typeof item.seatOffsetZ === "number" && Number.isFinite(item.seatOffsetZ)
        ? item.seatOffsetZ
        : 0,
    colorSlots,
    colorVariants,
    defaultColorVariantId,
    catalogCategory: isAdmin ? category : DRAFT_LIBRARY_CATEGORY_KEY,
    catalogCollection: isAdmin ? collection : DRAFT_LIBRARY_COLLECTION_KEY,
    itemState: isAdmin ? item.itemState ?? "published" : "draft",
  };
}

function isGlbFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "model/gltf-binary" || name.endsWith(".glb");
}

function getUint32LE(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

function getMissingTeleportClipNames(animationNames: string[]): string[] {
  const available = new Set(animationNames);
  return TELEPORT_REQUIRED_CLIP_NAMES.filter((name) => !available.has(name));
}

function getMissingDoorClipNames(animationNames: string[]): string[] {
  const available = new Set(animationNames);
  return DOOR_REQUIRED_CLIP_NAMES.filter((name) => !available.has(name));
}

function hasUnpairedTeleportTransitionClips(animationNames: string[]): boolean {
  const available = new Set(animationNames);
  const hasOpen = available.has("teleport_open");
  const hasClose = available.has("teleport_close");
  return hasOpen !== hasClose;
}

async function readGlbAnimationNames(file: File): Promise<string[]> {
  const payload = await file.arrayBuffer();
  if (payload.byteLength < 20) {
    return [];
  }
  const view = new DataView(payload);
  if (getUint32LE(view, 0) !== 0x46546c67) {
    return [];
  }
  if (getUint32LE(view, 4) !== 2) {
    return [];
  }
  let cursor = 12;
  let jsonChunk: Uint8Array | null = null;
  while (cursor + 8 <= payload.byteLength) {
    const chunkLength = getUint32LE(view, cursor);
    const chunkType = getUint32LE(view, cursor + 4);
    cursor += 8;
    if (cursor + chunkLength > payload.byteLength) {
      return [];
    }
    if (chunkType === 0x4e4f534a) {
      jsonChunk = new Uint8Array(payload, cursor, chunkLength);
      break;
    }
    cursor += chunkLength;
  }
  if (!jsonChunk) {
    return [];
  }
  try {
    const text = new TextDecoder().decode(jsonChunk).replace(/\u0000+$/g, "");
    const parsed = JSON.parse(text) as Record<string, unknown>;
    const animations = Array.isArray(parsed.animations)
      ? parsed.animations
      : [];
    const names = new Set<string>();
    for (const animation of animations) {
      if (!animation || typeof animation !== "object") {
        continue;
      }
      const name = (animation as Record<string, unknown>).name;
      if (typeof name === "string" && name.trim()) {
        names.add(name.trim());
      }
    }
    return Array.from(names);
  } catch {
    return [];
  }
}

function roundTo(value: number, precision: number): number {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function inferTileSpanFromMeters(sizeMeters: number): number {
  const safe = Number.isFinite(sizeMeters) ? Math.max(0, sizeMeters) : 0;
  // Tolerate tiny authoring noise while still preferring conservative coverage.
  return Math.max(
    1,
    Math.min(AUTO_DETECT_TILE_MAX, Math.ceil(Math.max(0, safe - 0.05)))
  );
}

function inferStackHeightFromMeters(heightMeters: number): number {
  const safe = Number.isFinite(heightMeters) ? Math.max(0, heightMeters) : 0;
  return Math.max(0, Math.min(AUTO_DETECT_STACK_HEIGHT_MAX, roundTo(safe, 3)));
}

async function detectGlbModelMetrics(
  file: File
): Promise<AutoDetectedModelMetrics | null> {
  const payload = await file.arrayBuffer();
  const loader = new GLTFLoader();
  const parsed = await new Promise<{ scene: THREE.Object3D }>(
    (resolve, reject) => {
      loader.parse(
        payload,
        "",
        (gltf) => resolve(gltf as { scene: THREE.Object3D }),
        (error) =>
          reject(
            error instanceof Error
              ? error
              : new Error("Could not parse GLB for auto-detection.")
          )
      );
    }
  );

  const scene = parsed.scene;
  if (!scene) {
    return null;
  }
  scene.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(scene, true);
  if (box.isEmpty()) {
    return null;
  }

  const size = new THREE.Vector3();
  box.getSize(size);
  const widthMeters = Math.max(0, size.x);
  const depthMeters = Math.max(0, size.z);
  const heightMeters = Math.max(0, size.y);
  const blockWidth = inferTileSpanFromMeters(widthMeters);
  const blockDepth = inferTileSpanFromMeters(depthMeters);
  const stackHeight = inferStackHeightFromMeters(heightMeters);
  const paletteSlotSet = new Set<string>();
  const paletteDefaultColors: Record<string, string> = {};
  scene.traverse((child) => {
    if (!(child as THREE.Mesh).isMesh) {
      return;
    }
    const mesh = child as THREE.Mesh;
    const materials = Array.isArray(mesh.material)
      ? mesh.material
      : [mesh.material];
    for (const material of materials) {
      const slot = paletteSlotFromMaterialName(material?.name);
      if (!slot) {
        continue;
      }
      paletteSlotSet.add(slot);
      if (!(slot in paletteDefaultColors)) {
        if ("color" in material && material.color instanceof THREE.Color) {
          paletteDefaultColors[slot] = `#${material.color.getHexString()}`;
        } else {
          paletteDefaultColors[slot] = "#ffffff";
        }
      }
    }
  });
  const paletteSlots = Array.from(paletteSlotSet);

  return {
    widthMeters: roundTo(widthMeters, 3),
    depthMeters: roundTo(depthMeters, 3),
    heightMeters: roundTo(heightMeters, 3),
    blockWidth,
    blockDepth,
    stackHeight,
    paletteSlots,
    paletteDefaultColors,
  };
}

function SpecRule({ children }: { children: React.ReactNode }) {
  return (
    <List.Item>
      <Text size="sm" lh={1.6}>
        {children}
      </Text>
    </List.Item>
  );
}

function CreatorSpecDocsPanel() {
  return (
    <Stack gap="lg">
      <Stack gap={2}>
        <Title order={3}>Furniture Designer Guide</Title>
        <Text size="sm" c="gray.7">
          Everything you need to know to create a GLB model and configure it in
          the Creator.
        </Text>
      </Stack>

      <Accordion
        variant="separated"
        radius="md"
        multiple
        defaultValue={["sizing", "glb", "interactions", "palette", "configure"]}
      >
        {/* 1. Sizing & Scale */}
        <Accordion.Item value="sizing">
          <Accordion.Control>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="orange">
                <Text size="xs" fw={700}>
                  1
                </Text>
              </ThemeIcon>
              <Text fw={600} size="sm">
                Sizing &amp; Scale
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Group gap="xs">
                <Badge variant="light" color="orange">
                  1 tile = 1 meter
                </Badge>
                <Badge variant="light" color="gray">
                  Max footprint: 8 x 8
                </Badge>
                <Badge variant="light" color="gray">
                  Max stack height: 8
                </Badge>
              </Group>
              <Text size="sm">
                Model your furniture in meters. A <Code>1x1</Code> chair is
                about 1&nbsp;m wide and 1&nbsp;m deep. The footprint you set in
                the Creator (<Code>blockWidth</Code> x <Code>blockDepth</Code>)
                determines how many tiles the item occupies &mdash; the mesh
                bounding box is not used for collision.
              </Text>
              <Text size="sm">
                <Code>stackHeight</Code> controls how tall the item is for
                stacking purposes. Set it to the surface height where you want
                other items to sit on top.
              </Text>

              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <Card p="xs" radius="sm" withBorder>
                  <Stack gap={4}>
                    <Text size="xs" fw={600}>
                      1x1, stack height 1
                    </Text>
                    <Box style={{ height: 140 }}>
                      <FurniturePreviewCanvas
                        modelUrl="/assets/default-cube.glb"
                        blockWidth={1}
                        blockDepth={1}
                        stackHeight={1}
                        inRoom
                      />
                    </Box>
                  </Stack>
                </Card>
                <Card p="xs" radius="sm" withBorder>
                  <Stack gap={4}>
                    <Text size="xs" fw={600}>
                      1x1, stack height 0.5
                    </Text>
                    <Box style={{ height: 140 }}>
                      <FurniturePreviewCanvas
                        modelUrl="/assets/half-cube.glb"
                        blockWidth={1}
                        blockDepth={1}
                        stackHeight={0.5}
                        inRoom
                      />
                    </Box>
                  </Stack>
                </Card>
                <Card p="xs" radius="sm" withBorder>
                  <Stack gap={4}>
                    <Text size="xs" fw={600}>
                      2x1 sofa
                    </Text>
                    <Box style={{ height: 140 }}>
                      <FurniturePreviewCanvas
                        modelUrl="/assets/half-cube-sofa-adjustable.glb"
                        blockWidth={2}
                        blockDepth={1}
                        stackHeight={0.5}
                        inRoom
                      />
                    </Box>
                  </Stack>
                </Card>
              </SimpleGrid>

              <Text size="xs" c="dimmed">
                Rotation is cardinal only (0 / 90 / 180 / 270). At 90 or 270 the
                footprint swaps: a 2x1 becomes 1x2.
              </Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* 2. GLB Export Rules */}
        <Accordion.Item value="glb">
          <Accordion.Control>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="red">
                <Text size="xs" fw={700}>
                  2
                </Text>
              </ThemeIcon>
              <Text fw={600} size="sm">
                GLB Export Rules
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="sm">
                Export as binary glTF 2.0 (<Code>.glb</Code>). The server
                rejects files that break any of these rules:
              </Text>
              <Table withRowBorders={false} verticalSpacing={6}>
                <Table.Tbody>
                  {(
                    [
                      [
                        "Max file size",
                        "1 MiB \u2014 smaller is better, optimize geometry and textures",
                      ],
                      ["Meshes", "At least one mesh required"],
                      ["Cameras", "Remove all cameras before export"],
                      [
                        "Armatures / Skins",
                        "Not allowed (use shape keys if needed)",
                      ],
                      ["Lights", "No lights (KHR_lights_punctual is rejected)"],
                      [
                        "Animations",
                        "Only allowed for door and teleport types",
                      ],
                    ] as const
                  ).map(([rule, detail]) => (
                    <Table.Tr key={rule}>
                      <Table.Td w={160}>
                        <Text size="sm" fw={600}>
                          {rule}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {detail}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Text size="xs" c="dimmed">
                In Blender: File &rarr; Export &rarr; glTF 2.0, format
                &ldquo;glTF Binary (.glb)&rdquo;. Uncheck cameras and lights in
                the export dialog.
              </Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* 3. Interaction Types & Animations */}
        <Accordion.Item value="interactions">
          <Accordion.Control>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="violet">
                <Text size="xs" fw={700}>
                  3
                </Text>
              </ThemeIcon>
              <Text fw={600} size="sm">
                Interaction Types &amp; Animations
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="sm">
                Choose an interaction type in the Creator. This determines what
                the GLB needs to contain:
              </Text>
              <Table withRowBorders={false} verticalSpacing={6}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      <Text size="xs" fw={600}>
                        Type
                      </Text>
                    </Table.Th>
                    <Table.Th>
                      <Text size="xs" fw={600}>
                        GLB Animations
                      </Text>
                    </Table.Th>
                    <Table.Th>
                      <Text size="xs" fw={600}>
                        Behavior
                      </Text>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr>
                    <Table.Td>
                      <Badge variant="dot" color="gray">
                        none
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="red.6" fw={500}>
                        Must not have any
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        Static decoration
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge variant="dot" color="blue">
                        sit
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="red.6" fw={500}>
                        Must not have any
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        Players sit. Forces walkable, not stackable. Use seat
                        offsets to position the player.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge variant="dot" color="blue">
                        lay
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="red.6" fw={500}>
                        Must not have any
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        Players lay down. Same rules as sit.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge variant="dot" color="violet">
                        door
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge size="sm" variant="light" color="teal">
                          door_open
                        </Badge>
                        <Badge size="sm" variant="light" color="teal">
                          door_close
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        Animated open/close. Exact clip names required.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td>
                      <Badge variant="dot" color="violet">
                        teleport
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Badge size="sm" variant="light" color="teal">
                          teleport_effects
                        </Badge>
                        <Text size="xs" c="dimmed">
                          Optional pair: <Code>teleport_open</Code> +{" "}
                          <Code>teleport_close</Code>
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        Warps players between linked instances. Optional
                        open/close pair must be both present or both absent.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* 4. Colorable Materials (PAL_) */}
        <Accordion.Item value="palette">
          <Accordion.Control>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="teal">
                <Text size="xs" fw={700}>
                  4
                </Text>
              </ThemeIcon>
              <Text fw={600} size="sm">
                Colorable Materials
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="sm">
                To let users pick colors, name your Blender materials with the{" "}
                <Code>PAL_</Code> prefix. For example, <Code>PAL_primary</Code>{" "}
                and <Code>PAL_accent</Code> create two color slots.
              </Text>
              <List size="sm" spacing={4}>
                <SpecRule>
                  Slot names are lowercased automatically. Must match{" "}
                  <Code>{"[a-z][a-z0-9_]{0,23}"}</Code>.
                </SpecRule>
                <SpecRule>
                  Max <strong>8</strong> color slots per item.
                </SpecRule>
                <SpecRule>
                  Max <strong>16</strong> color variants per item.
                </SpecRule>
                <SpecRule>
                  Every variant must provide a hex color for{" "}
                  <strong>every</strong> declared slot &mdash; missing or
                  unknown slots are rejected.
                </SpecRule>
                <SpecRule>
                  The server validates that each declared slot actually exists
                  as a <Code>PAL_</Code> material in the GLB. A mismatch is
                  rejected.
                </SpecRule>
                <SpecRule>
                  If any color slots exist, at least one variant is required and{" "}
                  <Code>defaultColorVariantId</Code> must be set.
                </SpecRule>
              </List>

              <Card p="xs" radius="sm" withBorder>
                <Stack gap={4}>
                  <Text size="xs" fw={600}>
                    Example: sofa with <Code>PAL_primary</Code> in two color
                    variants
                  </Text>
                  <SimpleGrid cols={2} spacing="xs">
                    <Box style={{ height: 140 }}>
                      <FurniturePreviewCanvas
                        modelUrl="/assets/half-cube-sofa-adjustable.glb"
                        blockWidth={2}
                        blockDepth={1}
                        stackHeight={0.5}
                        inRoom
                        colorVariants={CREATOR_EXAMPLE_COLOR_VARIANTS}
                        defaultColorVariantId="beige"
                        colorVariantId="beige"
                      />
                    </Box>
                    <Box style={{ height: 140 }}>
                      <FurniturePreviewCanvas
                        modelUrl="/assets/half-cube-sofa-adjustable.glb"
                        blockWidth={2}
                        blockDepth={1}
                        stackHeight={0.5}
                        inRoom
                        colorVariants={CREATOR_EXAMPLE_COLOR_VARIANTS}
                        defaultColorVariantId="beige"
                        colorVariantId="teal"
                      />
                    </Box>
                  </SimpleGrid>
                  <Group gap="xs" justify="center">
                    <Badge size="sm" variant="light" color="orange">
                      Beige variant
                    </Badge>
                    <Badge size="sm" variant="light" color="teal">
                      Teal variant
                    </Badge>
                  </Group>
                </Stack>
              </Card>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* 5. Configuring in Creator */}
        <Accordion.Item value="configure">
          <Accordion.Control>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="blue">
                <Text size="xs" fw={700}>
                  5
                </Text>
              </ThemeIcon>
              <Text fw={600} size="sm">
                Configuring in the Creator
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="sm">
                After dropping your GLB, the Creator auto-detects footprint,
                stack height, and color slots from the model. Review and tweak
                these values, then fill in the metadata:
              </Text>

              <Card p="sm" radius="sm" withBorder>
                <Stack gap="xs">
                  <Text fw={700} size="sm" c="dark">
                    Required Fields
                  </Text>
                  <Table withRowBorders={false} verticalSpacing={4}>
                    <Table.Tbody>
                      {(
                        [
                          ["name", "1 \u2013 64 characters"],
                          ["priceCoins", "Integer, 1 \u2013 1,000,000"],
                          ["blockWidth", "Integer, 1 \u2013 8"],
                          ["blockDepth", "Integer, 1 \u2013 8"],
                        ] as const
                      ).map(([field, constraint]) => (
                        <Table.Tr key={field}>
                          <Table.Td w={130}>
                            <Code>{field}</Code>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {constraint}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>

              <Card p="sm" radius="sm" withBorder>
                <Stack gap="xs">
                  <Text fw={700} size="sm" c="dark">
                    Key Optional Fields
                  </Text>
                  <Table withRowBorders={false} verticalSpacing={4}>
                    <Table.Tbody>
                      {(
                        [
                          ["description", "\u2264 512 characters"],
                          ["artist", "\u2264 64 characters"],
                          [
                            "stackHeight",
                            "0 \u2013 8 (surface height for stacking)",
                          ],
                          ["walkableTop", "Players can walk on top"],
                          ["stackableTop", "Other items can stack on top"],
                          [
                            "seatOffsetX / Y / Z",
                            "-1.5 \u2013 1.5 (sit/lay only)",
                          ],
                        ] as const
                      ).map(([field, note]) => (
                        <Table.Tr key={field}>
                          <Table.Td w={170}>
                            <Code>{field}</Code>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {note}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Card>

              <Card p="sm" radius="sm" withBorder>
                <Stack gap="xs">
                  <Text fw={700} size="sm" c="dark">
                    Automatic Behavior
                  </Text>
                  <List size="sm" spacing={4}>
                    <SpecRule>
                      <Code>sit</Code> and <Code>lay</Code> automatically enable{" "}
                      <Code>walkableTop</Code> and disable{" "}
                      <Code>stackableTop</Code>.
                    </SpecRule>
                    <SpecRule>
                      <Code>door</Code> and <Code>teleport</Code> automatically
                      enable <Code>walkableTop</Code>.
                    </SpecRule>
                    <SpecRule>
                      Non-admin saves are always drafts under{" "}
                      <strong>My Creations</strong>. Admins can publish directly
                      to the catalog.
                    </SpecRule>
                  </List>
                </Stack>
              </Card>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* 6. Quick Checklist */}
        <Accordion.Item value="checklist">
          <Accordion.Control>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="teal">
                <Text size="xs" fw={700}>
                  &#10003;
                </Text>
              </ThemeIcon>
              <Text fw={600} size="sm">
                Quick Checklist
              </Text>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <List size="sm" spacing={6} type="ordered">
              <List.Item>
                <Text size="sm">
                  Model at <Code>1 m = 1 tile</Code>. Set{" "}
                  <Code>blockWidth</Code> / <Code>blockDepth</Code> to match.
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm">
                  Export <Code>.glb</Code> &mdash; no cameras, lights, or
                  armatures.
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm">
                  For <Code>none</Code> / <Code>sit</Code> / <Code>lay</Code>:
                  make sure the GLB has <strong>no animation clips</strong>.
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm">
                  For <Code>door</Code>: include exactly <Code>door_open</Code>{" "}
                  and <Code>door_close</Code> clips.
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm">
                  For <Code>teleport</Code>: include{" "}
                  <Code>teleport_effects</Code>. Optionally add both{" "}
                  <Code>teleport_open</Code> and <Code>teleport_close</Code>.
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm">
                  Name colorable materials <Code>PAL_slotname</Code> in Blender.
                  Set up color variants in the Creator.
                </Text>
              </List.Item>
              <List.Item>
                <Text size="sm">
                  Hit <strong>Validate Spec</strong> before publishing.
                </Text>
              </List.Item>
            </List>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}
export function CreatorScreen({
  session,
  onCreated,
  editingFurniture = null,
  onDoneEditing,
}: Props) {
  const [activeTab, setActiveTab] = useState<"designer" | "docs">("designer");
  const [draft, setDraft] = useState<CreatorDraft>(() =>
    createDefaultDraft(session.address, session.isAdmin)
  );
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [autoDetectedMetrics, setAutoDetectedMetrics] =
    useState<AutoDetectedModelMetrics | null>(null);
  const [glbAnimationNames, setGlbAnimationNames] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewRotation, setPreviewRotation] = useState(0);
  const [selectedPalettePreviewVariantId, setSelectedPalettePreviewVariantId] =
    useState("");
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [validationState, setValidationState] = useState<{
    bytes: number;
    checksumSha256: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const animationParseTokenRef = useRef(0);
  const editingFurnitureId = editingFurniture?.id ?? null;
  const isEditing = editingFurniture != null;

  useEffect(() => {
    if (editingFurniture) {
      setDraft(
        createDraftFromFurniture(
          editingFurniture,
          session.address,
          session.isAdmin
        )
      );
    } else {
      setDraft(createDefaultDraft(session.address, session.isAdmin));
    }
    setPreviewFile(null);
    setAutoDetectedMetrics(null);
    setGlbAnimationNames([]);
    setValidationState(null);
    setPreviewRotation(0);
    setSelectedPalettePreviewVariantId("");
  }, [editingFurniture, editingFurnitureId, session.address, session.isAdmin]);

  useEffect(() => {
    if (draft.colorVariants.length === 0) {
      setSelectedPalettePreviewVariantId("");
      return;
    }
    if (
      selectedPalettePreviewVariantId &&
      draft.colorVariants.some(
        (variant) => variant.id === selectedPalettePreviewVariantId
      )
    ) {
      return;
    }
    const fallback =
      draft.colorVariants.find(
        (variant) => variant.id === draft.defaultColorVariantId
      )?.id ??
      draft.colorVariants[0]?.id ??
      "";
    setSelectedPalettePreviewVariantId(fallback);
  }, [
    draft.colorVariants,
    draft.defaultColorVariantId,
    selectedPalettePreviewVariantId,
  ]);

  useEffect(() => {
    if (!previewFile) {
      setPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(previewFile);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setPreviewUrl("");
    };
  }, [previewFile]);

  const previewSource = previewUrl || draft.modelUrl.trim();
  const isSeatMode =
    draft.interactionType === "sit" || draft.interactionType === "lay";
  const isTeleportMode = draft.interactionType === "teleport";
  const isDoorMode = draft.interactionType === "door";
  const hasLocalClipData = previewFile != null;
  const missingDoorClipNames = useMemo(
    () =>
      isDoorMode && hasLocalClipData
        ? getMissingDoorClipNames(glbAnimationNames)
        : [],
    [glbAnimationNames, hasLocalClipData, isDoorMode]
  );
  const missingTeleportClipNames = useMemo(
    () =>
      isTeleportMode && hasLocalClipData
        ? getMissingTeleportClipNames(glbAnimationNames)
        : [],
    [glbAnimationNames, hasLocalClipData, isTeleportMode]
  );
  const unpairedTeleportTransitionClips = useMemo(
    () =>
      isTeleportMode && hasLocalClipData
        ? hasUnpairedTeleportTransitionClips(glbAnimationNames)
        : false,
    [glbAnimationNames, hasLocalClipData, isTeleportMode]
  );
  const effectiveWalkableTop =
    isSeatMode || isDoorMode || isTeleportMode || draft.walkableTop;
  const effectiveStackableTop = isSeatMode ? false : draft.stackableTop;
  const effectiveItemState: FurnitureItemState = session.isAdmin
    ? draft.itemState
    : "draft";
  const effectiveCatalogCategory = session.isAdmin
    ? normalizeCatalogCategoryKey(
        draft.catalogCategory || DEFAULT_FURNITURE_CATALOG_CATEGORY_KEY
      )
    : DRAFT_LIBRARY_CATEGORY_KEY;
  const effectiveCatalogCollection = session.isAdmin
    ? normalizeCatalogCollectionKey(
        draft.catalogCollection || DEFAULT_FURNITURE_CATALOG_COLLECTION_KEY,
        effectiveCatalogCategory
      )
    : DRAFT_LIBRARY_COLLECTION_KEY;

  const metadataPayload = useMemo(() => {
    const colorSlots = normalizeColorSlotList(draft.colorSlots);
    const colorVariants: FurnitureColorVariant[] = draft.colorVariants
      .slice(0, PALETTE_VARIANT_MAX)
      .map((variant, index) => {
        const fallbackName = `Variant ${index + 1}`;
        const colors: Record<string, string> = {};
        for (const slot of colorSlots) {
          const normalizedColor = normalizePaletteHex(
            variant.colors[slot] ?? ""
          );
          colors[slot] = normalizedColor ?? "#ffffff";
        }
        return {
          id: variant.id,
          name: variant.name.trim() || fallbackName,
          colors,
          priceCoins: Math.max(
            1,
            Math.round(
              typeof variant.priceCoins === "number" &&
                Number.isFinite(variant.priceCoins)
                ? variant.priceCoins
                : draft.priceCoins
            )
          ),
          active: variant.active !== false,
        };
      });
    const defaultColorVariantId =
      colorVariants.find(
        (variant) => variant.id === draft.defaultColorVariantId
      )?.id ??
      colorVariants[0]?.id ??
      undefined;
    const payload: Omit<AdminFurnitureInput, "id" | "modelUrl"> & {
      description?: string;
      modelUrl?: string;
    } = {
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      priceCoins: Math.round(draft.priceCoins),
      artist: draft.artist.trim() || undefined,
      blockWidth: Math.max(1, Math.round(draft.blockWidth)),
      blockDepth: Math.max(1, Math.round(draft.blockDepth)),
      sittable: isSeatMode,
      interactionType: draft.interactionType,
      walkableTop: effectiveWalkableTop,
      stackableTop: effectiveStackableTop,
      stackHeight: draft.stackHeight,
      actionOpenAnimation: isDoorMode ? DOOR_OPEN_CLIP_NAME : undefined,
      actionCloseAnimation: isDoorMode ? DOOR_CLOSE_CLIP_NAME : undefined,
      seatOffsetX: isSeatMode ? draft.seatOffsetX : 0,
      seatOffsetY: isSeatMode ? draft.seatOffsetY : 0,
      seatOffsetZ: isSeatMode ? draft.seatOffsetZ : 0,
      colorSlots,
      colorVariants,
      defaultColorVariantId,
      modelUrl: draft.modelUrl.trim() || undefined,
      catalogCategory: effectiveCatalogCategory,
      catalogCollection: effectiveCatalogCollection,
      itemState: effectiveItemState,
    };
    return payload;
  }, [
    draft,
    effectiveCatalogCategory,
    effectiveCatalogCollection,
    effectiveItemState,
    effectiveStackableTop,
    effectiveWalkableTop,
    isDoorMode,
    isSeatMode,
  ]);
  const catalogCollectionOptions = useMemo(
    () =>
      listCollectionsForCategory(effectiveCatalogCategory).map((entry) => ({
        value: entry.key,
        label: entry.name,
      })),
    [effectiveCatalogCategory]
  );

  const readFile = (file: File | null) => {
    animationParseTokenRef.current += 1;
    const parseToken = animationParseTokenRef.current;
    if (!file) {
      setPreviewFile(null);
      setAutoDetectedMetrics(null);
      setGlbAnimationNames([]);
      return;
    }
    if (!isGlbFile(file)) {
      notifications.show({
        title: "Invalid file",
        message: "Only .glb files are supported.",
        color: "red",
      });
      return;
    }
    setPreviewFile(file);
    setAutoDetectedMetrics(null);
    setGlbAnimationNames([]);
    setValidationState(null);
    void detectGlbModelMetrics(file)
      .then((metrics) => {
        if (animationParseTokenRef.current !== parseToken) {
          return;
        }
        if (!metrics) {
          setAutoDetectedMetrics(null);
          return;
        }
        setAutoDetectedMetrics(metrics);
        setDraft((prev) => {
          const nextSlots = normalizeColorSlotList(metrics.paletteSlots);
          const nextVariants =
            nextSlots.length === 0
              ? []
              : prev.colorVariants.length > 0
              ? prev.colorVariants
                  .slice(0, PALETTE_VARIANT_MAX)
                  .map((variant) =>
                    normalizeVariantForSlots(
                      variant,
                      nextSlots,
                      metrics.paletteDefaultColors
                    )
                  )
              : [
                  createColorVariantDraft(
                    nextSlots,
                    metrics.paletteDefaultColors,
                    prev.priceCoins,
                    "Base Variant"
                  ),
                ];
          const nextDefaultVariantId =
            nextVariants.find(
              (variant) => variant.id === prev.defaultColorVariantId
            )?.id ??
            nextVariants[0]?.id ??
            "";
          return {
            ...prev,
            blockWidth: metrics.blockWidth,
            blockDepth: metrics.blockDepth,
            stackHeight: metrics.stackHeight,
            colorSlots: nextSlots,
            colorVariants: nextVariants,
            defaultColorVariantId: nextDefaultVariantId,
          };
        });
      })
      .catch(() => {
        if (animationParseTokenRef.current !== parseToken) {
          return;
        }
        setAutoDetectedMetrics(null);
      });
    void readGlbAnimationNames(file)
      .then((names) => {
        if (animationParseTokenRef.current !== parseToken) {
          return;
        }
        setGlbAnimationNames(names);
      })
      .catch(() => {
        if (animationParseTokenRef.current !== parseToken) {
          return;
        }
        setGlbAnimationNames([]);
      });
  };

  const handleInputFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    readFile(file);
  };

  const handleDropFile = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0] ?? null;
    readFile(file);
  };

  const validate = async () => {
    if (!previewFile) {
      throw new Error("Drop a .glb file first.");
    }
    if (isDoorMode && missingDoorClipNames.length > 0) {
      throw new Error(
        `Door GLB must include clip names: ${missingDoorClipNames.join(", ")}.`
      );
    }
    if (isTeleportMode && missingTeleportClipNames.length > 0) {
      throw new Error(
        `Teleport GLB must include clip names: ${missingTeleportClipNames.join(
          ", "
        )}.`
      );
    }
    if (isTeleportMode && unpairedTeleportTransitionClips) {
      throw new Error(
        "Teleport clip names teleport_open and teleport_close must be both present or both absent."
      );
    }
    setValidating(true);
    try {
      const result = await validateAdminFurnitureModel(
        session,
        previewFile,
        metadataPayload
      );
      setValidationState({
        bytes: result.bytes,
        checksumSha256: result.checksumSha256,
      });
      notifications.show({
        title: "Spec validation passed",
        message: `${result.bytes.toLocaleString()} bytes`,
        color: "teal",
      });
    } finally {
      setValidating(false);
    }
  };

  const publish = async () => {
    if (!isEditing && !previewFile) {
      notifications.show({
        title: "Missing model",
        message: "Drop a .glb file before publishing.",
        color: "red",
      });
      return;
    }
    if (hasLocalClipData && isDoorMode && missingDoorClipNames.length > 0) {
      notifications.show({
        title: "Missing door clips",
        message: `Door GLB must include: ${missingDoorClipNames.join(", ")}`,
        color: "red",
      });
      return;
    }
    if (
      hasLocalClipData &&
      isTeleportMode &&
      missingTeleportClipNames.length > 0
    ) {
      notifications.show({
        title: "Missing teleport clips",
        message: `Teleport GLB must include: ${missingTeleportClipNames.join(
          ", "
        )}`,
        color: "red",
      });
      return;
    }
    if (hasLocalClipData && isTeleportMode && unpairedTeleportTransitionClips) {
      notifications.show({
        title: "Invalid teleport clips",
        message:
          "teleport_open and teleport_close must be both present or both absent.",
        color: "red",
      });
      return;
    }
    setPublishing(true);
    try {
      let modelUrl = draft.modelUrl.trim();
      if (previewFile) {
        const upload = await uploadAdminFurnitureModel(
          session,
          previewFile,
          metadataPayload
        );
        modelUrl = upload.url;
      }
      if (!modelUrl) {
        throw new Error("Model URL is required.");
      }
      const publishPayload: AdminFurnitureInput = {
        ...metadataPayload,
        modelUrl,
        priceCoins: Math.round(metadataPayload.priceCoins),
        blockWidth: Math.max(1, Math.round(metadataPayload.blockWidth)),
        blockDepth: Math.max(1, Math.round(metadataPayload.blockDepth)),
      };

      if (isEditing && editingFurniture) {
        const updated = await updateAdminFurniture(
          session,
          editingFurniture.id,
          publishPayload
        );
        notifications.show({
          title:
            effectiveItemState === "draft"
              ? "Draft saved"
              : "Furniture updated",
          message: updated.name,
          color: "teal",
        });
        await onCreated();
        setDraft(
          createDraftFromFurniture(updated, session.address, session.isAdmin)
        );
        setPreviewFile(null);
        setAutoDetectedMetrics(null);
        setValidationState(null);
        if (onDoneEditing) {
          onDoneEditing();
        }
      } else {
        const created = await createAdminFurniture(session, publishPayload);
        notifications.show({
          title:
            effectiveItemState === "draft"
              ? "Draft saved"
              : "Furniture published",
          message: created.name,
          color: "teal",
        });
        await onCreated();
        setDraft(createDefaultDraft(session.address, session.isAdmin));
        setPreviewFile(null);
        setAutoDetectedMetrics(null);
        setPreviewRotation(0);
        setValidationState(null);
      }
    } catch (error) {
      notifications.show({
        title: "Publish failed",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Tabs
          value={activeTab}
          onChange={(value) =>
            setActiveTab((value as "designer" | "docs") ?? "designer")
          }
        >
          <Tabs.List>
            <Tabs.Tab value="designer">Designer</Tabs.Tab>
            <Tabs.Tab value="docs">Spec Docs</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {activeTab === "docs" ? (
          <CreatorSpecDocsPanel />
        ) : (
          <>
            <Stack gap={2}>
              <Title order={2} c="dark">
                {isEditing ? "Edit Furniture" : "Creator"}
              </Title>
              <Text size="sm" c="gray.7">
                {isEditing
                  ? "Edit metadata and optionally replace the GLB model, then save back to the catalog."
                  : session.isAdmin
                  ? "Start by dragging a GLB. Validate against the spec, then upload and publish to the furniture catalog."
                  : "Start by dragging a GLB. Your saves become sandbox drafts under My Creations and are not for sale."}
              </Text>
              {isEditing && editingFurniture ? (
                <Badge variant="light" color="orange" w="fit-content">
                  Editing ID: {editingFurniture.id}
                </Badge>
              ) : null}
            </Stack>

            <SimpleGrid
              cols={{ base: 1, md: 2 }}
              spacing="md"
              verticalSpacing="md"
            >
              <Card
                p="sm"
                radius="md"
                style={{ border: "1px solid rgba(0,0,0,0.08)" }}
              >
                <Stack gap="sm">
                  <Box
                    p="sm"
                    style={{
                      width: "100%",
                      border: "2px dashed rgba(0,0,0,0.15)",
                      borderRadius: 10,
                      textAlign: "center",
                      background: "rgba(255,255,255,0.6)",
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleDropFile}
                  >
                    <Text size="sm" fw={600} c="dark">
                      {isEditing
                        ? "Drop a GLB to replace the current model (optional)"
                        : "Drag and drop a GLB to begin"}
                    </Text>
                    <Text size="xs" c="dimmed" mb="xs">
                      {isEditing
                        ? "Spec checks apply to any newly dropped GLB before upload."
                        : "The validator checks file structure and metadata rules before upload."}
                    </Text>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose GLB file
                    </Button>
                    <Text size="xs" c="gray.6" mt="xs">
                      {previewFile
                        ? `Selected: ${previewFile.name}`
                        : isEditing
                        ? "Using current saved model URL"
                        : "No file selected"}
                    </Text>
                    {autoDetectedMetrics ? (
                      <Text size="xs" c="teal.7" mt={4}>
                        Auto-detected from GLB bounds:{" "}
                        {autoDetectedMetrics.blockWidth}×
                        {autoDetectedMetrics.blockDepth} tiles, stack height{" "}
                        {autoDetectedMetrics.stackHeight}m (
                        {autoDetectedMetrics.widthMeters}m ×{" "}
                        {autoDetectedMetrics.depthMeters}m ×{" "}
                        {autoDetectedMetrics.heightMeters}m)
                        {autoDetectedMetrics.paletteSlots.length > 0
                          ? `, palette slots: ${autoDetectedMetrics.paletteSlots.join(
                              ", "
                            )}.`
                          : "."}
                      </Text>
                    ) : null}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".glb,model/gltf-binary"
                      onChange={handleInputFile}
                      style={{ display: "none" }}
                    />
                  </Box>

                  <Group grow>
                    <TextInput
                      label="Name"
                      value={draft.name}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          name: event.currentTarget.value,
                        }))
                      }
                    />
                    <TextInput
                      label="Artist"
                      value={draft.artist}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          artist: event.currentTarget.value,
                        }))
                      }
                    />
                  </Group>

                  <Textarea
                    label="Description"
                    minRows={2}
                    value={draft.description}
                    onChange={(event) =>
                      setDraft((prev) => ({
                        ...prev,
                        description: event.currentTarget.value,
                      }))
                    }
                  />

                  <Group grow>
                    <NumberInput
                      label="Price (coins)"
                      min={1}
                      value={draft.priceCoins}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          priceCoins:
                            typeof value === "number" ? value : prev.priceCoins,
                        }))
                      }
                    />
                  </Group>

                  <Group grow>
                    <Select
                      label="Category"
                      value={effectiveCatalogCategory}
                      disabled={!session.isAdmin}
                      data={FURNITURE_CATALOG_CATEGORIES.map((category) => ({
                        value: category.key,
                        label: category.name,
                      }))}
                      onChange={(value) => {
                        if (!session.isAdmin || !value) {
                          return;
                        }
                        const category = normalizeCatalogCategoryKey(value);
                        const nextCollection = normalizeCatalogCollectionKey(
                          draft.catalogCollection,
                          category
                        );
                        setDraft((prev) => ({
                          ...prev,
                          catalogCategory: category,
                          catalogCollection: nextCollection,
                        }));
                      }}
                    />
                    <Select
                      label="Collection"
                      value={effectiveCatalogCollection}
                      disabled={!session.isAdmin}
                      data={catalogCollectionOptions}
                      onChange={(value) => {
                        if (!session.isAdmin || !value) {
                          return;
                        }
                        setDraft((prev) => ({
                          ...prev,
                          catalogCollection: normalizeCatalogCollectionKey(
                            value,
                            effectiveCatalogCategory
                          ),
                        }));
                      }}
                    />
                  </Group>

                  {session.isAdmin ? (
                    <Stack gap={4}>
                      <Text size="sm" fw={600} c="gray.7">
                        Item State
                      </Text>
                      <SegmentedControl
                        value={effectiveItemState}
                        onChange={(value) =>
                          setDraft((prev) => ({
                            ...prev,
                            itemState: value as FurnitureItemState,
                          }))
                        }
                        data={[
                          { label: "Published", value: "published" },
                          { label: "Draft", value: "draft" },
                        ]}
                      />
                    </Stack>
                  ) : (
                    <Badge variant="light" color="orange" w="fit-content">
                      Saved as sandbox draft in My Creations
                    </Badge>
                  )}

                  <Group grow>
                    <NumberInput
                      label="Block Width"
                      min={1}
                      max={8}
                      value={draft.blockWidth}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          blockWidth:
                            typeof value === "number"
                              ? Math.max(1, Math.round(value))
                              : prev.blockWidth,
                        }))
                      }
                    />
                    <NumberInput
                      label="Block Depth"
                      min={1}
                      max={8}
                      value={draft.blockDepth}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          blockDepth:
                            typeof value === "number"
                              ? Math.max(1, Math.round(value))
                              : prev.blockDepth,
                        }))
                      }
                    />
                    <NumberInput
                      label="Stack Height"
                      min={0}
                      max={8}
                      decimalScale={2}
                      value={draft.stackHeight}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          stackHeight:
                            typeof value === "number"
                              ? value
                              : prev.stackHeight,
                        }))
                      }
                    />
                  </Group>

                  <Stack gap={4}>
                    <Text size="sm" fw={600} c="gray.7">
                      Interaction Type
                    </Text>
                    <SegmentedControl
                      value={draft.interactionType}
                      onChange={(value) =>
                        setDraft((prev) => ({
                          ...prev,
                          interactionType: value as FurnitureInteractionType,
                        }))
                      }
                      data={[
                        { label: "None", value: "none" },
                        { label: "Sit", value: "sit" },
                        { label: "Lay", value: "lay" },
                        { label: "Door", value: "door" },
                        { label: "Teleport", value: "teleport" },
                      ]}
                    />
                  </Stack>

                  <Group grow>
                    <Switch
                      label="Walkable Top"
                      checked={effectiveWalkableTop}
                      disabled={isSeatMode || isDoorMode || isTeleportMode}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          walkableTop: event.currentTarget.checked,
                        }))
                      }
                    />
                    <Switch
                      label="Stackable Top"
                      checked={effectiveStackableTop}
                      disabled={isSeatMode}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          stackableTop: event.currentTarget.checked,
                        }))
                      }
                    />
                  </Group>

                  {isSeatMode ? (
                    <Stack gap={4}>
                      <Group grow>
                        <NumberInput
                          label="Seat Offset X"
                          decimalScale={2}
                          value={draft.seatOffsetX}
                          onChange={(value) =>
                            setDraft((prev) => ({
                              ...prev,
                              seatOffsetX:
                                typeof value === "number"
                                  ? value
                                  : prev.seatOffsetX,
                            }))
                          }
                        />
                        <NumberInput
                          label="Seat Offset Y"
                          decimalScale={2}
                          value={draft.seatOffsetY}
                          onChange={(value) =>
                            setDraft((prev) => ({
                              ...prev,
                              seatOffsetY:
                                typeof value === "number"
                                  ? value
                                  : prev.seatOffsetY,
                            }))
                          }
                        />
                        <NumberInput
                          label="Seat Offset Z"
                          decimalScale={2}
                          value={draft.seatOffsetZ}
                          onChange={(value) =>
                            setDraft((prev) => ({
                              ...prev,
                              seatOffsetZ:
                                typeof value === "number"
                                  ? value
                                  : prev.seatOffsetZ,
                            }))
                          }
                        />
                      </Group>
                      <Text size="xs" c="dimmed">
                        Seat facing is derived from furniture rotation. Author
                        seat direction in the GLB.
                      </Text>
                    </Stack>
                  ) : null}

                  {isDoorMode ? (
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed">
                        Door mode uses fixed GLB clip names:{" "}
                        <code>{DOOR_OPEN_CLIP_NAME}</code> and{" "}
                        <code>{DOOR_CLOSE_CLIP_NAME}</code>.
                      </Text>
                      {hasLocalClipData ? (
                        <Group gap="xs">
                          {DOOR_REQUIRED_CLIP_NAMES.map((clipName) => {
                            const present =
                              glbAnimationNames.includes(clipName);
                            return (
                              <Badge
                                key={clipName}
                                color={present ? "teal" : "red"}
                                variant="light"
                              >
                                {clipName}
                              </Badge>
                            );
                          })}
                        </Group>
                      ) : (
                        <Text size="xs" c="dimmed">
                          Drop a GLB to validate door clips locally before
                          saving.
                        </Text>
                      )}
                    </Stack>
                  ) : null}

                  {isTeleportMode ? (
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed">
                        Teleport mode requires fixed GLB clip name:{" "}
                        <code>{TELEPORT_EFFECTS_CLIP_NAME}</code>. Optional
                        clips: <code>teleport_open</code> and{" "}
                        <code>teleport_close</code> as a pair.
                      </Text>
                      {hasLocalClipData ? (
                        <Group gap="xs">
                          {TELEPORT_REQUIRED_CLIP_NAMES.map((clipName) => {
                            const present =
                              glbAnimationNames.includes(clipName);
                            return (
                              <Badge
                                key={clipName}
                                color={present ? "teal" : "red"}
                                variant="light"
                              >
                                {clipName}
                              </Badge>
                            );
                          })}
                          <Badge
                            color={
                              unpairedTeleportTransitionClips ? "red" : "teal"
                            }
                            variant="light"
                          >
                            teleport_open/teleport_close pair
                          </Badge>
                        </Group>
                      ) : (
                        <Text size="xs" c="dimmed">
                          Drop a GLB to validate teleport clips locally before
                          saving.
                        </Text>
                      )}
                    </Stack>
                  ) : null}

                  {draft.colorSlots.length > 0 ? (
                    <Card
                      p="xs"
                      radius="md"
                      style={{ border: "1px solid rgba(0,0,0,0.08)" }}
                    >
                      <Stack gap="xs">
                        <Stack gap={2}>
                          <Text size="sm" fw={600} c="gray.8">
                            Color Variations
                          </Text>
                          <Text size="xs" c="dimmed">
                            Adjustable materials are detected from GLB material
                            names using prefix <code>PAL_</code> (example:{" "}
                            <code>PAL_primary</code>).
                          </Text>
                        </Stack>
                        <Group gap={4}>
                          {draft.colorSlots.map((slot) => (
                            <Badge key={slot} variant="outline" color="teal">
                              {slot}
                            </Badge>
                          ))}
                        </Group>

                        {draft.colorVariants.map((variant, index) => (
                          <Card
                            key={variant.id}
                            p="xs"
                            radius="sm"
                            style={{
                              border: "1px solid rgba(0,0,0,0.08)",
                              background:
                                variant.id === draft.defaultColorVariantId
                                  ? "rgba(64,192,87,0.06)"
                                  : "#fff",
                            }}
                          >
                            <Stack gap="xs">
                              <Group justify="space-between" align="end">
                                <TextInput
                                  label={`Variant ${index + 1} Name`}
                                  value={variant.name}
                                  onChange={(event) =>
                                    setDraft((prev) => ({
                                      ...prev,
                                      colorVariants: prev.colorVariants.map(
                                        (candidate) =>
                                          candidate.id === variant.id
                                            ? {
                                                ...candidate,
                                                name: event.currentTarget.value,
                                              }
                                            : candidate
                                      ),
                                    }))
                                  }
                                />
                                <NumberInput
                                  label="Variant Price"
                                  min={1}
                                  max={1_000_000}
                                  value={variant.priceCoins}
                                  onChange={(value) =>
                                    setDraft((prev) => ({
                                      ...prev,
                                      colorVariants: prev.colorVariants.map(
                                        (candidate) =>
                                          candidate.id === variant.id
                                            ? {
                                                ...candidate,
                                                priceCoins: Math.max(
                                                  1,
                                                  Math.round(
                                                    typeof value === "number" &&
                                                      Number.isFinite(value)
                                                      ? value
                                                      : candidate.priceCoins
                                                  )
                                                ),
                                              }
                                            : candidate
                                      ),
                                    }))
                                  }
                                />
                                <Switch
                                  label="Listed in store"
                                  checked={variant.active !== false}
                                  onChange={(event) =>
                                    setDraft((prev) => ({
                                      ...prev,
                                      colorVariants: prev.colorVariants.map(
                                        (candidate) =>
                                          candidate.id === variant.id
                                            ? {
                                                ...candidate,
                                                active:
                                                  event.currentTarget.checked,
                                              }
                                            : candidate
                                      ),
                                    }))
                                  }
                                />
                                <Group gap="xs">
                                  <Button
                                    size="xs"
                                    variant={
                                      variant.id === draft.defaultColorVariantId
                                        ? "filled"
                                        : "light"
                                    }
                                    color={
                                      variant.id === draft.defaultColorVariantId
                                        ? "teal"
                                        : "gray"
                                    }
                                    onClick={() =>
                                      setDraft((prev) => ({
                                        ...prev,
                                        defaultColorVariantId: variant.id,
                                      }))
                                    }
                                  >
                                    Default
                                  </Button>
                                  <Button
                                    size="xs"
                                    variant="light"
                                    color="red"
                                    disabled={draft.colorVariants.length <= 1}
                                    onClick={() =>
                                      setDraft((prev) => {
                                        const next = prev.colorVariants.filter(
                                          (candidate) =>
                                            candidate.id !== variant.id
                                        );
                                        const nextDefault =
                                          next.find(
                                            (candidate) =>
                                              candidate.id ===
                                              prev.defaultColorVariantId
                                          )?.id ??
                                          next[0]?.id ??
                                          "";
                                        return {
                                          ...prev,
                                          colorVariants: next,
                                          defaultColorVariantId: nextDefault,
                                        };
                                      })
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Group>
                              </Group>

                              <Group grow>
                                {draft.colorSlots.map((slot) => (
                                  <ColorInput
                                    key={`${variant.id}-${slot}`}
                                    label={slot}
                                    value={variant.colors[slot] ?? "#ffffff"}
                                    onChange={(value) =>
                                      setDraft((prev) => ({
                                        ...prev,
                                        colorVariants: prev.colorVariants.map(
                                          (candidate) => {
                                            if (candidate.id !== variant.id) {
                                              return candidate;
                                            }
                                            const normalized =
                                              normalizePaletteHex(value) ??
                                              candidate.colors[slot] ??
                                              "#ffffff";
                                            return {
                                              ...candidate,
                                              colors: {
                                                ...candidate.colors,
                                                [slot]: normalized,
                                              },
                                            };
                                          }
                                        ),
                                      }))
                                    }
                                    format="hex"
                                    swatches={[
                                      "#ffffff",
                                      "#f8f9fa",
                                      "#ced4da",
                                      "#343a40",
                                      "#e03131",
                                      "#f08c00",
                                      "#2f9e44",
                                      "#1971c2",
                                      "#ae3ec9",
                                    ]}
                                  />
                                ))}
                              </Group>
                            </Stack>
                          </Card>
                        ))}

                        <Group justify="space-between">
                          <Text size="xs" c="dimmed">
                            Variants: {draft.colorVariants.length}
                          </Text>
                          <Button
                            size="xs"
                            variant="light"
                            disabled={
                              draft.colorVariants.length >= PALETTE_VARIANT_MAX
                            }
                            onClick={() =>
                              setDraft((prev) => {
                                const slots = normalizeColorSlotList(
                                  prev.colorSlots
                                );
                                if (slots.length === 0) {
                                  return prev;
                                }
                                const baseColors =
                                  prev.colorVariants.find(
                                    (variant) =>
                                      variant.id === prev.defaultColorVariantId
                                  )?.colors ?? {};
                                const nextVariant = createColorVariantDraft(
                                  slots,
                                  baseColors,
                                  prev.priceCoins,
                                  `Variant ${prev.colorVariants.length + 1}`
                                );
                                return {
                                  ...prev,
                                  colorVariants: [
                                    ...prev.colorVariants,
                                    nextVariant,
                                  ].slice(0, PALETTE_VARIANT_MAX),
                                };
                              })
                            }
                          >
                            Add Variant
                          </Button>
                        </Group>
                      </Stack>
                    </Card>
                  ) : null}
                </Stack>
              </Card>

              <Card
                p="sm"
                radius="md"
                style={{ border: "1px solid rgba(0,0,0,0.08)" }}
              >
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={600}>Preview</Text>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() =>
                          setPreviewRotation((prev) => (prev + 270) % 360)
                        }
                      >
                        -90°
                      </Button>
                      <Badge variant="light">{previewRotation}°</Badge>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() =>
                          setPreviewRotation((prev) => (prev + 90) % 360)
                        }
                      >
                        +90°
                      </Button>
                    </Group>
                  </Group>
                  {draft.colorVariants.length > 0 ? (
                    <SegmentedControl
                      size="xs"
                      value={selectedPalettePreviewVariantId}
                      onChange={setSelectedPalettePreviewVariantId}
                      data={draft.colorVariants.map((variant, index) => ({
                        label: variant.name.trim() || `Variant ${index + 1}`,
                        value: variant.id,
                      }))}
                    />
                  ) : null}

                  <Box style={{ height: "clamp(360px, 62vh, 760px)" }}>
                    {previewSource ? (
                      <FurniturePreviewCanvas
                        modelUrl={previewSource}
                        inRoom
                        interactive
                        rotation={previewRotation}
                        blockWidth={draft.blockWidth}
                        blockDepth={draft.blockDepth}
                        interactionType={draft.interactionType}
                        walkableTop={effectiveWalkableTop}
                        stackableTop={effectiveStackableTop}
                        stackHeight={draft.stackHeight}
                        actionOpenAnimation={
                          isDoorMode ? DOOR_OPEN_CLIP_NAME : undefined
                        }
                        actionCloseAnimation={
                          isDoorMode ? DOOR_CLOSE_CLIP_NAME : undefined
                        }
                        sittable={isSeatMode}
                        seatOffsetX={draft.seatOffsetX}
                        seatOffsetY={draft.seatOffsetY}
                        seatOffsetZ={draft.seatOffsetZ}
                        colorVariants={metadataPayload.colorVariants}
                        defaultColorVariantId={
                          draft.defaultColorVariantId || undefined
                        }
                        colorVariantId={
                          selectedPalettePreviewVariantId ||
                          draft.defaultColorVariantId
                        }
                      />
                    ) : (
                      <Group
                        justify="center"
                        align="center"
                        h="100%"
                        style={{
                          border: "1px dashed rgba(0,0,0,0.15)",
                          borderRadius: 8,
                          color: "#71717a",
                        }}
                      >
                        <Text size="sm">Drop a GLB file to start preview.</Text>
                      </Group>
                    )}
                  </Box>

                  {validationState ? (
                    <Badge color="teal" variant="light">
                      Spec passed: {validationState.bytes.toLocaleString()}{" "}
                      bytes
                    </Badge>
                  ) : null}

                  <Group justify="space-between">
                    <Group gap="xs">
                      <Button
                        variant="light"
                        loading={validating}
                        disabled={!previewFile}
                        onClick={() =>
                          void validate().catch((error) => {
                            notifications.show({
                              title: "Validation failed",
                              message:
                                error instanceof Error
                                  ? error.message
                                  : "Please retry.",
                              color: "red",
                            });
                          })
                        }
                      >
                        Validate Spec
                      </Button>
                      {isEditing && onDoneEditing ? (
                        <Button variant="default" onClick={onDoneEditing}>
                          Back to Catalog
                        </Button>
                      ) : null}
                    </Group>
                    <Button loading={publishing} onClick={() => void publish()}>
                      {isEditing
                        ? previewFile
                          ? effectiveItemState === "draft"
                            ? "Upload and Save Draft"
                            : "Upload and Save"
                          : effectiveItemState === "draft"
                          ? "Save Draft"
                          : "Save Changes"
                        : effectiveItemState === "draft"
                        ? "Upload and Save Draft"
                        : "Upload and Publish"}
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </SimpleGrid>
          </>
        )}
      </Stack>
    </Container>
  );
}
